import { useEffect, useRef, useState } from 'react';
import tw, { styled } from 'twin.macro';
import 'styled-components/macro';

import { Terminal } from 'component-lib/organisms/terminal/Terminal';
import { Button } from 'play/atoms/button/Button';
import { BrowserTabPlaceholder } from 'play/molecules/placeholders/browser-tab-placeholder/BrowserTabPlaceholder';
import { EmptyLabPlaceholder } from 'play/molecules/placeholders/empty-lab-placeholder/EmptyLabPlaceholder';
import { ServiceTabPlaceholder } from 'play/molecules/placeholders/service-tab-placeholder/ServiceTabPlaceholder';
import { TerminalTabPlaceholder } from 'play/molecules/placeholders/terminal-tab-placeholder/TerminalTabPlaceholder';
import { WebsiteTabPlaceholder } from 'play/molecules/placeholders/website-tab-placeholder/WebsiteTabPlaceholder';
import { useTrackAmplitudeEvent } from 'support/amplitude/amplitudeEvent';
import { ChallengeTabType } from 'types/graphql';

interface WindowProps {
  active: boolean;
  isPreview?: boolean;
}

const Window = styled.div<WindowProps>`
  flex: 1;
  position: absolute;
  left: ${props => (props.active ? '0' : '-100vw')};
  font-size: ${props => (props.isPreview ? '16px' : '0')};
  width: 100%;
  height: 100%;
`;

const CentralizedView = styled.div`
  line-height: 4em;
  flex-direction: column;
  font-size: 20px;
  text-align: center;
  background-color: #1c2639;
  color: white;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface FrameProps {
  isResizing: boolean;
  type: ChallengeTabType;
}

// We need to disable pointer events on service iframes when we are resizing the sidebar.
// Otherwise the iframe would capture the mouse up events and not propagate them to the resize bar handler.
const Frame = styled.iframe<FrameProps>`
  pointer-events: ${props => (props.isResizing ? 'none' : 'inherit')};
  padding: ${props => (props.type === ChallengeTabType.Terminal ? '10px' : 0)};
  ${props => (props.type === ChallengeTabType.Terminal ? tw`bg-[#1C2639]` : tw`bg-white`)};
  width: 100%;
  height: 100%;
  border: 0;
  font-size: 17px;
  line-height: 19px;
  color: #ffffff;
  font-weight: 300;
  box-sizing: border-box;
  z-index: 1;
  position: relative;
`;

interface OverlayProps {
  visible: boolean;
}

const Overlay = styled.div<OverlayProps>`
  display: ${props => (props.visible ? 'block' : 'none')};
  background-color: transparent;
  width: 100%;
  height: 100%;
  position: absolute;
  opacity: 0;
  z-index: 2;
  left: 0;
  top: 0;
`;

export interface ServiceProps {
  active: boolean;
  type: ChallengeTabType;
  url: string | null;
  serviceID: string;
  newWindow?: boolean;
  tabID: string;
  captureFocus?: boolean;
  isResizing?: boolean;
  isPreview?: boolean;
  useNewTerminal?: boolean;
}

export const Service = ({
  isPreview,
  active,
  type,
  url,
  serviceID,
  newWindow = false,
  tabID,
  captureFocus = false,
  isResizing = false,
  useNewTerminal = false,
}: ServiceProps) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const externalWindow = useRef<Window | null>(null);
  const trackAmplitudeEvent = useTrackAmplitudeEvent();

  const openInNewWindow = () => {
    if (externalWindow.current && !externalWindow.current.closed) {
      externalWindow.current.focus();
      trackAmplitudeEvent({ name: 'Lab - Challenge - Opened external window', data: { action: 'focused' } });
    } else if (url) {
      externalWindow.current = window.open(url, `tab-${tabID}`);
      trackAmplitudeEvent({ name: 'Lab - Challenge - Opened external window', data: { action: 'opened' } });
    }
  };

  useEffect(() => {
    if (newWindow) {
      trackAmplitudeEvent({ name: 'Lab - Challenge - External window button shown' });
    }
  }, [trackAmplitudeEvent, newWindow]);

  useEffect(() => {
    if ((type !== ChallengeTabType.Service && type !== ChallengeTabType.Browser) || !active || !captureFocus) return;

    const refocus = (event: MouseEvent) => {
      if (overlayRef.current) {
        if (event.target !== overlayRef.current) {
          setOverlayVisible(true);
          return;
        } else {
          setOverlayVisible(false);
        }
      }

      if (iframeRef.current) {
        // Explicitly refocus on the iframe here to make sure keyboard focus is moved to the iframe.
        // This solves the issue described at: https://guacamole.apache.org/faq/#iframes.
        iframeRef.current.focus();
      }
    };

    document.addEventListener('click', refocus, false);
    document.addEventListener('mousemove', refocus, false);

    return () => {
      document.removeEventListener('click', refocus, false);
      document.removeEventListener('mousemove', refocus, false);
    };
  }, [active, type, captureFocus, serviceID, url]);

  if (isPreview) {
    switch (type) {
      case ChallengeTabType.External:
      case ChallengeTabType.Website:
        return (
          <Window isPreview active={active}>
            <WebsiteTabPlaceholder title={url ?? ''} />
          </Window>
        );
      case ChallengeTabType.Service:
        return (
          <Window isPreview active={active}>
            <ServiceTabPlaceholder title={url ?? ''} />
          </Window>
        );
      case ChallengeTabType.Browser:
        return (
          <Window isPreview active={active}>
            <BrowserTabPlaceholder title={url ?? ''} />
          </Window>
        );
      case ChallengeTabType.Terminal:
        return (
          <Window isPreview active={active}>
            <TerminalTabPlaceholder />
          </Window>
        );
      default:
        return (
          <Window isPreview active={active}>
            <EmptyLabPlaceholder />
          </Window>
        );
    }
  }

  return (
    <Window active={active}>
      {newWindow ? (
        // Note: This view is only visible if the service tab is the first tab
        // For >=2nd tabs, clicking them will open the new window immediately
        <CentralizedView>
          <p>This tab needs to be loaded in an external window.</p>
          <Button onClick={openInNewWindow}>Open external window</Button>
        </CentralizedView>
      ) : useNewTerminal && type === ChallengeTabType.Terminal && url ? (
        <Terminal url={url} tw="w-full h-full" />
      ) : (
        <>
          <Frame
            ref={iframeRef}
            allow="clipboard-read; clipboard-write;"
            sandbox="allow-popups-to-escape-sandbox allow-same-origin allow-scripts allow-popups allow-forms"
            referrerPolicy="no-referrer"
            type={type}
            src={url || undefined}
            id={serviceID}
            name={`${serviceID}-${Date.now()}`}
            isResizing={isResizing}
          />
          {captureFocus && <Overlay ref={overlayRef} visible={overlayVisible} />}
        </>
      )}
    </Window>
  );
};
